// Port of load_bmo_chequing_pdf in scripts/loaders.py.
//
// Architecturally different from the Python original: that one calls
// `pdftotext -layout` and then operates on character-column positions of
// the resulting text. We can't reliably reproduce that output via PDF.js,
// so this implementation works DIRECTLY on PDF.js text items (x/y/str).
// The end-result Transaction objects must still match the Python loader's
// output — fixture-driven tests are the cross-language oracle.
//
// Algorithm:
//   1. Extract structured lines from the PDF.
//   2. Find the "Date Description from your account ($) to your account ($)
//      Balance ($)" header line — its item x-positions define the column
//      boundaries for debit / credit / balance.
//   3. Pull statement period (year + closing month) from a "For the period
//      ending <Month> <Day>, <Year>" string anywhere on page 1.
//   4. For each non-header line whose first item matches "MMM DD" (date
//      column), classify any numeric items by their x-position into
//      amount (debit OR credit) and balance, then synthesize a Transaction.
//   5. Year disambiguation: a Dec row on a January statement is from the
//      PRIOR year — mirrors the Python REG 2 fix.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";
import { extractPdfLines, type PdfItem, type PdfLine } from "../pdf.js";

const MONTH_MAP: Record<string, number> = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

const NUM_RE = /^[\d,]+\.\d{2}$/;
const DATE_RE = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/;
const FULL_DATE_RE = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})/;

// Pull the statement year and closing month from anywhere in the PDF text.
// Mirrors _statement_year + _statement_closing_month in scripts/loaders.py.
function findStatementPeriod(allText: string): { year: number; month: number } {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  let year = new Date().getFullYear();
  let month = 0;
  const re = /period\s+ending\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+(\d{4})/i;
  const m = allText.match(re);
  if (m) {
    month = months.findIndex((mm) => mm.toLowerCase() === m[1].toLowerCase()) + 1;
    const y = parseInt(m[2], 10);
    if (y >= 2000 && y <= 2100) year = y;
  }
  return { year, month };
}

// Find the column boundary x-positions from the header row.
// PDF.js delivers multi-word column headers as SINGLE items
// (e.g. "from your account ($)" is one item), so we match by substring
// within each item rather than across items.
function findColumnBoundaries(lines: PdfLine[]): {
  debitX: number;
  creditX: number;
  balanceX: number;
} | null {
  for (const line of lines) {
    const joined = line.items.map((i) => i.str).join(" ").toLowerCase();
    if (!joined.includes("from your account") || !joined.includes("balance")) {
      continue;
    }
    let debitX = -1;
    let creditX = -1;
    let balanceX = -1;
    for (const it of line.items) {
      const s = it.str.toLowerCase();
      if (debitX < 0 && s.includes("from your")) debitX = it.x;
      if (creditX < 0 && s.includes("to your")) creditX = it.x;
      if (balanceX < 0 && s.includes("balance")) balanceX = it.x;
    }
    if (debitX > 0 && creditX > 0 && balanceX > 0) {
      return { debitX, creditX, balanceX };
    }
  }
  return null;
}

// Try to find an account number anywhere in the document. Mirrors the
// "Account # …" regex in load_bmo_chequing_pdf. Returns last 4 digits.
function findAccountLast4(lines: PdfLine[]): string {
  for (const line of lines) {
    const text = line.items.map((i) => i.str).join(" ");
    const m = text.match(/Account\s*#\s*([\d][\d \-]*?\d)(?:\s{2,}|$|\s*\(continued\))/);
    if (m) return extractLast4(m[1]);
  }
  return "";
}

interface RawTxLine {
  date: { mon: string; day: number };
  description: string;
  debit: number;
  credit: number;
}

// Extract candidate transaction lines. Each line whose first item is a
// "Jan|Feb|..." date token (and second is a 1-2 digit day) becomes a
// candidate. Adjacent items between the date and the first numeric token
// form the description; numeric tokens are classified by x-position.
function extractTxLines(
  lines: PdfLine[],
  cols: { debitX: number; creditX: number; balanceX: number },
): RawTxLine[] {
  const out: RawTxLine[] = [];
  // Items mid-line that look like dollars: at least 2 digits + .NN.
  // We require classification against the known column x-positions:
  //   amount column = (debitX..creditX) → debit
  //   amount column = (creditX..balanceX) → credit
  //   amount column ≥ balanceX → balance (skipped — we only emit txns)
  const colMid = (a: number, b: number) => (a + b) / 2;
  const debitMid = colMid(cols.debitX, cols.creditX);
  const creditMid = colMid(cols.creditX, cols.balanceX);

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    if (line.items.length < 2) continue;
    const a = line.items[0];
    const b = line.items[1];
    if (!DATE_RE.test(a.str)) continue;
    const day = parseInt(b.str, 10);
    if (!Number.isFinite(day) || day < 1 || day > 31) continue;

    // Numeric items in this line.
    const nums: { x: number; value: number }[] = [];
    const nonNumIdx: number[] = [];
    for (let k = 2; k < line.items.length; k++) {
      const it = line.items[k];
      if (NUM_RE.test(it.str)) {
        nums.push({ x: it.x, value: parseAmount(it.str) });
      } else {
        nonNumIdx.push(k);
      }
    }
    if (nums.length === 0) continue;

    // Description = all non-numeric items in reading order.
    const descParts: string[] = [];
    for (const idx of nonNumIdx) descParts.push(line.items[idx].str);
    let description = descParts.join(" ").trim();

    // Description wrap: if the next line has no date and no numeric items
    // and starts to the right of the date column (x > date item's x by a bit),
    // it's a continuation. Mirrors the Python pairs-pass.
    const next = lines[li + 1];
    if (
      next &&
      next.items.length > 0 &&
      !DATE_RE.test(next.items[0].str) &&
      !next.items.some((it: PdfItem) => NUM_RE.test(it.str))
    ) {
      const cont = next.items.map((it: PdfItem) => it.str).join(" ").trim();
      if (cont.length > 0 && cont.length <= 60) {
        description = `${description} ${cont}`.trim();
      }
    }
    if (!description) continue;
    if (/^(opening balance|closing total)/i.test(description)) continue;

    // Classify the first amount (debit or credit) by x-position.
    // The LAST numeric item is the balance — we ignore it for tx emission.
    let debit = 0;
    let credit = 0;
    for (let n = 0; n < nums.length - 1; n++) {
      const num = nums[n];
      if (Math.abs(num.x - debitMid) <= Math.abs(num.x - creditMid)) {
        debit = num.value;
      } else {
        credit = num.value;
      }
    }
    if (debit === 0 && credit === 0) continue;

    out.push({ date: { mon: a.str, day }, description, debit, credit });
  }
  return out;
}

export async function loadBmoChequingPdf(
  data: ArrayBuffer | Uint8Array,
  filename: string,
): Promise<Transaction[]> {
  const lines = await extractPdfLines(data);
  if (lines.length === 0) return [];

  // Flatten all text for period/account-number scanning.
  const allText = lines
    .map((l) => l.items.map((i) => i.str).join(" "))
    .join("\n");

  const { year: closingYear, month: closingMonth } = findStatementPeriod(allText);
  const cols = findColumnBoundaries(lines);
  if (!cols) return [];
  const pdfLast4 = findAccountLast4(lines);

  const raw = extractTxLines(lines, cols);
  const out: Transaction[] = [];
  for (const r of raw) {
    if (isInterPortalTransfer(r.description)) continue;
    // Sign convention: positive = expense. Debit (money out) → positive,
    // Credit (money in) → negative.
    const signed = r.debit > 0 ? r.debit : -r.credit;
    const monNum = MONTH_MAP[r.date.mon];
    // Year disambiguation: Dec rows on a January statement belong to the
    // PRIOR year. Mirrors REG 2.
    const txYear =
      closingMonth && monNum > closingMonth ? closingYear - 1 : closingYear;
    const dateStr = `${txYear.toString().padStart(4, "0")}-${monNum
      .toString()
      .padStart(2, "0")}-${r.date.day.toString().padStart(2, "0")}`;

    const id = await makeId("bmo", "BMO Chequing", dateStr, signed, r.description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: r.description,
        vendor_key: normalizeVendor(r.description),
        amount: Math.round(signed * 100) / 100,
        portal: "bmo",
        account: "BMO Chequing",
        account_kind: "debit",
        account_last4: pdfLast4,
        source_file: filename,
        raw: { line: `${r.date.mon} ${r.date.day} ${r.description}` },
      }),
    );
  }
  return out;
}

// Re-exports for the dispatcher / tests.
export { FULL_DATE_RE };

// ---------- BMO Mastercard PDF ----------

const DATE_DOT_RE = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\.?$/;

// "Statement date Apr. 3, 2026" → { year: 2026, month: 4 }.
function findMcStatementPeriod(allText: string): { year: number; month: number } {
  let year = new Date().getFullYear();
  let month = 0;
  const m = allText.match(
    /Statement\s+date\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\.?\s+\d{1,2},?\s+(\d{4})/i,
  );
  if (m) {
    month = MONTH_MAP[m[1].slice(0, 3) as keyof typeof MONTH_MAP] ?? 0;
    const y = parseInt(m[2], 10);
    if (y >= 2000 && y <= 2100) year = y;
  }
  return { year, month };
}

// "Card number XXXX XXXX XXXX 7610" → "7610".
function findMcLast4(lines: PdfLine[]): string {
  for (const line of lines) {
    const text = line.items.map((i) => i.str).join(" ");
    const m = text.match(/Card\s+number[:\s]*[X\s]+(\d{4})/);
    if (m) return m[1];
  }
  return "";
}

export async function loadBmoMastercardPdf(
  data: ArrayBuffer | Uint8Array,
  filename: string,
): Promise<Transaction[]> {
  const lines = await extractPdfLines(data);
  if (lines.length === 0) return [];

  const allText = lines
    .map((l) => l.items.map((i) => i.str).join(" "))
    .join("\n");
  const { year: closingYear, month: closingMonth } = findMcStatementPeriod(allText);
  const mcLast4 = findMcLast4(lines);

  const out: Transaction[] = [];
  for (const line of lines) {
    // Need at least: trans_mon, trans_day, post_mon, post_day, desc, amount.
    if (line.items.length < 6) continue;
    const tMonItem = line.items[0];
    const tDayItem = line.items[1];
    const pMonItem = line.items[2];
    const pDayItem = line.items[3];
    if (!DATE_DOT_RE.test(tMonItem.str)) continue;
    if (!DATE_DOT_RE.test(pMonItem.str)) continue;
    const tDay = parseInt(tDayItem.str, 10);
    const pDay = parseInt(pDayItem.str, 10);
    if (!Number.isFinite(tDay) || tDay < 1 || tDay > 31) continue;
    if (!Number.isFinite(pDay) || pDay < 1 || pDay > 31) continue;

    // Description = items[4..n-1]. Last item is the amount (with optional CR).
    const last = line.items[line.items.length - 1];
    const amtMatch = last.str.match(/^([\d,]+\.\d{2})(?:\s*(CR))?$/);
    if (!amtMatch) continue;
    let amount = parseAmount(amtMatch[1]);
    if (amtMatch[2]) amount = -amount;

    const descItems = line.items.slice(4, line.items.length - 1);
    if (descItems.length === 0) continue;
    const description = descItems
      .map((i) => i.str)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    if (!description) continue;
    if (isInterPortalTransfer(description)) continue;

    const tMonStr = tMonItem.str.replace(/\.$/, "");
    const monNum = MONTH_MAP[tMonStr];
    if (!monNum) continue;
    const txYear =
      closingMonth && monNum > closingMonth ? closingYear - 1 : closingYear;
    const dateStr = `${txYear.toString().padStart(4, "0")}-${monNum
      .toString()
      .padStart(2, "0")}-${tDay.toString().padStart(2, "0")}`;

    const id = await makeId("bmo", "BMO Mastercard", dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description),
        amount: Math.round(amount * 100) / 100,
        portal: "bmo",
        account: "BMO Mastercard",
        account_kind: "liability",
        account_last4: mcLast4,
        source_file: filename,
        raw: { line: line.items.map((i) => i.str).join(" ") },
      }),
    );
  }
  return out;
}
